import React from 'react';

import { enqueueSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@infoods/lifebase-ui';

import classes from './RecipeForm.module.scss';
import { HiddenInput } from '../../../../shared/components/form-components';
import { openConfirmationModal } from '../../../../shared/features/confirmation';
import { goTo } from '../../../../shared/utils/angularUtils';
import { recipesService } from '../../../services/recipesService';
import { RecipeFormInitialData } from '../interfaces/RecipeFormInitialData';
import { RecipeFormValues } from '../interfaces/RecipeFormValues';
import { buildRecipeFormData } from '../utils/buildRecipeFormData/buildRecipeFormData';
import { IngredientsCard } from '../IngredientsCard/IngredientsCard';
import { RecipeDetailsCard } from '../RecipeDetailsCard/RecipeDetailsCard';
import { RecipeMacrosCard } from '../RecipeMacrosCard/RecipeMacrosCard';

type RecipeFormProps = {
  /** read once on mount - the form is not reset when it changes */
  initialData: RecipeFormInitialData;
  /** an editable loaded recipe - shows Delete */
  isEdit: boolean;
};

const RecipeForm = ({ initialData, isEdit }: RecipeFormProps) => {
  const { recipe, defaultValues, servingMeasurementOptions } = initialData;

  const formMethods = useForm<RecipeFormValues>({ defaultValues });
  const {
    handleSubmit,
    getValues,
    register,
    formState: { isSubmitting },
  } = formMethods;

  const goToList = () => goTo('/recipes');

  const handleSave = handleSubmit(async (formValues) => {
    if (!formValues.ingredients.length) {
      enqueueSnackbar('The recipe should have at least one ingredient', {
        variant: 'error',
      });
      return;
    }

    const formData = buildRecipeFormData(formValues);

    // PUT on a recipe the caller cannot edit saves a copy (clone from `/add-recipe/:id`)
    await (formValues.id != null
      ? recipesService.updateRecipe(formData)
      : recipesService.createRecipe(formData));

    enqueueSnackbar(`${formValues.name} saved`, { variant: 'success' });
    goToList();
  });

  const handleDeleteConfirm = async (id: number, name: string) => {
    await recipesService.deleteRecipe(id);

    enqueueSnackbar(`${name} deleted`, { variant: 'success' });
    goToList();
  };

  const handleDeleteClick = (id: number) => {
    const name = getValues('name') || defaultValues.name;

    openConfirmationModal({
      title: 'Are you sure you want to delete this recipe?',
      confirmBtnConfig: { onClick: () => handleDeleteConfirm(id, name), text: 'Delete' },
    });
  };

  const recipeId = defaultValues.id;

  return (
    <FormProvider {...formMethods}>
      <form noValidate onSubmit={handleSave}>
        <HiddenInput register={register} name="id" />
        <RecipeDetailsCard
          servingMeasurementOptions={servingMeasurementOptions}
          savedImageUrl={recipe?.image ?? null}
        />
        <IngredientsCard />
        <RecipeMacrosCard variant="perServing" />
        <RecipeMacrosCard variant="total" />
        <div className={classes.actions}>
          {isEdit && recipeId != null && (
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDeleteClick(recipeId)}
              data-test-id="delete-recipe-btn"
            >
              Delete
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting}
            data-test-id="save-recipe-btn"
          >
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export { RecipeForm };
